import { useCallback, useMemo, useRef, useState } from "react";
import { useScope } from "@/context/ScopeContext";
import subDistMap from "@/components/puneSubDistPaths.json";
import { deriveMapConfidence } from "@/utils/analyticsConfidence";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import "../pages/AnalyticsBlockMapTab.css";

const METRIC_OPTIONS = [
  { value: "classroom_health", label: "Classroom Health %" },
  { value: "toilet_functional", label: "Toilet Functional %" },
  { value: "apaar_rate", label: "APAAR Rate %" },
  { value: "teacher_quality", label: "Teacher Quality (CTET %)" },
  { value: "shi_score", label: "Overall School Health" },
];

const norm = (s) =>
  String(s || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const BLOCK_TO_TALUKA = {
  MAWAL: "MAVAL",
  PURANDAR: "PURANDHAR",
  PURANDHAR: "PURANDHAR",
  HADPASAR: "HAVELI",
  HADAPSAR: "HAVELI",
  BIBWEWADI: "HAVELI",
  YERAWADA: "HAVELI",
  AUNDH: "HAVELI",
  PIMPRI: "HAVELI",
  AKURDI: "HAVELI",
};

const talukaKey = (blockName) => {
  const n = norm(blockName);
  return BLOCK_TO_TALUKA[n] || n;
};

const rollupBlocksToTalukas = (blocks, metric) => {
  const groups = new Map();
  (blocks || []).forEach((b) => {
    const key = talukaKey(b.block_name);
    const val = b?.[metric];
    const numeric = typeof val === "number" && Number.isFinite(val) ? val : null;
    const weight = Number(b.students) || Number(b.schools) || Number(b.total_schools) || 1;
    if (!groups.has(key)) {
      groups.set(key, {
        block_name: key,
        block_code: b.block_code,
        schools: 0,
        students: 0,
        shi_score: 0,
        _weighted: 0,
        _weight: 0,
        _shiWeighted: 0,
        _shiWeight: 0,
      });
    }
    const g = groups.get(key);
    g.schools += Number(b.schools) || Number(b.total_schools) || 0;
    g.students += Number(b.students) || 0;
    if (numeric !== null) {
      g._weighted += numeric * weight;
      g._weight += weight;
    }
    const shi = b.shi_score;
    if (typeof shi === "number" && Number.isFinite(shi)) {
      g._shiWeighted += shi * weight;
      g._shiWeight += weight;
    }
    if (!g.block_code && b.block_code) g.block_code = b.block_code;
  });

  return Array.from(groups.values()).map((g) => ({
    ...g,
    [metric]: g._weight > 0 ? Math.round((g._weighted / g._weight) * 10) / 10 : null,
    shi_score: g._shiWeight > 0 ? Math.round((g._shiWeighted / g._shiWeight) * 10) / 10 : null,
  }));
};

export const getStatusBand = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return { label: "No data", className: "nodata", statusClass: "" };
  }
  if (value >= 90) return { label: "High", className: "high", statusClass: "aabm-status-high" };
  if (value >= 80) return { label: "Medium", className: "medium", statusClass: "aabm-status-medium" };
  if (value >= 70) return { label: "Low", className: "low", statusClass: "aabm-status-low" };
  return { label: "Critical", className: "critical", statusClass: "aabm-status-critical" };
};

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const fmtTime = () =>
  new Date().toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

const SUBDISTRICTS = subDistMap?.subdistricts || {};
const VIEWBOX = subDistMap?.viewBox || [0, 0, 800, 648];
const [VB_X, VB_Y, VB_W, VB_H] = VIEWBOX;
const MAP_CENTER_X = VB_X + VB_W / 2;
const MAP_CENTER_Y = VB_Y + VB_H / 2;
const ZOOM_MIN = 0.75;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;

const AnalyticsBlockMapTab = ({
  mapData = [],
  selectedMetric,
  onMetricChange,
  onRefresh,
  scopeLabel = "Pune District",
  scope = {},
}) => {
  const { setScope } = useScope();
  const isNarrowScope = Boolean(scope.blockCode || scope.udiseCode);
  const scopeHint = scope.schoolName || scope.blockName || "";
  const mapAreaRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const mapTransform = useMemo(
    () =>
      `translate(${MAP_CENTER_X} ${MAP_CENTER_Y}) scale(${zoom}) translate(${-MAP_CENTER_X} ${-MAP_CENTER_Y})`,
    [zoom],
  );

  const handleZoomIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100));
  }, []);

  const handleZoomOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100));
  }, []);

  const handleZoomReset = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom(1);
  }, []);

  const metricMeta = METRIC_OPTIONS.find((m) => m.value === selectedMetric) || METRIC_OPTIONS[0];
  const metricShort = metricMeta.label.replace(" %", "").replace(" (CTET %)", "");

  const talukaRows = useMemo(() => rollupBlocksToTalukas(mapData, selectedMetric), [mapData, selectedMetric]);

  const dataByName = useMemo(() => {
    const m = new Map();
    talukaRows.forEach((b) => m.set(norm(b.block_name), b));
    return m;
  }, [talukaRows]);

  const regions = useMemo(
    () => Object.entries(SUBDISTRICTS).map(([key, meta]) => ({ key, ...meta })),
    [],
  );

  const metricValue = useCallback(
    (regionKey) => {
      const b = dataByName.get(norm(regionKey));
      const v = b?.[selectedMetric];
      return typeof v === "number" && Number.isFinite(v) ? v : null;
    },
    [dataByName, selectedMetric],
  );

  const analysisRows = useMemo(
    () => (isNarrowScope ? mapData : talukaRows),
    [isNarrowScope, mapData, talukaRows],
  );

  const rowLabel = useCallback(
    (row) => {
      if (!row) return "—";
      if (isNarrowScope) return row.block_name;
      return SUBDISTRICTS[row.block_name]?.label || row.block_name;
    },
    [isNarrowScope],
  );

  const sortedRows = useMemo(() => {
    return [...analysisRows]
      .filter((t) => t[selectedMetric] != null)
      .sort((a, b) => (b[selectedMetric] ?? 0) - (a[selectedMetric] ?? 0));
  }, [analysisRows, selectedMetric]);

  const monitoredCount = isNarrowScope ? sortedRows.length : regions.length;

  const avgMetric = useMemo(() => {
    if (!sortedRows.length) return 0;
    const sum = sortedRows.reduce((s, t) => s + (t[selectedMetric] ?? 0), 0);
    return Math.round((sum / sortedRows.length) * 10) / 10;
  }, [sortedRows, selectedMetric]);

  const totalSchools = useMemo(
    () => analysisRows.reduce((s, t) => s + (t.schools || 0), 0),
    [analysisRows],
  );

  const needsAction = useMemo(
    () =>
      sortedRows.filter((t) => {
        const band = getStatusBand(t[selectedMetric]);
        return band.className === "critical" || band.className === "low";
      }),
    [sortedRows, selectedMetric],
  );

  const criticalCount = useMemo(
    () => sortedRows.filter((t) => getStatusBand(t[selectedMetric]).className === "critical").length,
    [sortedRows, selectedMetric],
  );

  const worst = sortedRows[sortedRows.length - 1];
  const best = sortedRows[0];
  const lowBlocks = sortedRows.filter((t) => {
    const c = getStatusBand(t[selectedMetric]).className;
    return c === "low" || c === "critical";
  });
  const highBlocks = sortedRows.filter((t) => getStatusBand(t[selectedMetric]).className === "high");

  const unitLabel = isNarrowScope ? "blocks" : "talukas";

  const readout = useMemo(() => {
    if (!sortedRows.length) {
      return scopeHint
        ? `No ${metricShort.toLowerCase()} metrics found for ${scopeHint}. Try clearing filters or refreshing data.`
        : "Loading block performance for the selected district scope.";
    }
    const worstName = rowLabel(worst) || "priority blocks";
    const bestName = rowLabel(best) || "top blocks";
    if (isNarrowScope && sortedRows.length === 1) {
      return `${worstName} is the selected scope with ${metricShort.toLowerCase()} at ${worst[selectedMetric]}%.`;
    }
    if (criticalCount > 0) {
      return `Overall performance is stable, but ${worstName} needs immediate ${metricShort.toLowerCase()} intervention.`;
    }
    if (needsAction.length > 0) {
      return `${needsAction.length} ${unitLabel} need near-term follow-up; ${bestName} remains the strongest performer.`;
    }
    return `${scopeLabel} ${metricShort.toLowerCase()} is stable across ${unitLabel} with ${highBlocks.length} above the high-performance threshold.`;
  }, [
    sortedRows,
    worst,
    best,
    criticalCount,
    needsAction,
    highBlocks,
    metricShort,
    scopeHint,
    isNarrowScope,
    scopeLabel,
    unitLabel,
    rowLabel,
    selectedMetric,
  ]);

  const aiInsight = useMemo(() => {
    if (!worst) return "Awaiting block metrics for the current filter scope.";
    const worstName = rowLabel(worst);
    const bestName = rowLabel(best);
    if (isNarrowScope && sortedRows.length === 1) {
      const band = getStatusBand(worst[selectedMetric]);
      return (
        <>
          <b style={{ color: "var(--aabm-text)" }}>
            {worstName} scores {worst[selectedMetric]}% on {metricShort.toLowerCase()} within the current filter.
          </b>{" "}
          Status is <b style={{ color: "var(--aabm-text)" }}>{band.label}</b>
          {scope.schoolName ? ` for ${scope.schoolName}` : scope.blockName ? ` in ${scope.blockName} block` : ""}.
        </>
      );
    }
    const criticalOnly = sortedRows.filter((t) => getStatusBand(t[selectedMetric]).className === "critical");
    if (criticalOnly.length === 1) {
      return (
        <>
          <b style={{ color: "var(--aabm-text)" }}>
            {rowLabel(criticalOnly[0])} is the only critical {unitLabel.slice(0, -1)} in the current {metricShort.toLowerCase()} view.
          </b>{" "}
          {lowBlocks.length} low-performing {unitLabel} need near-term inspection, while {highBlocks.length} are already
          above the high-performance threshold.
        </>
      );
    }
    return (
      <>
        <b style={{ color: "var(--aabm-text)" }}>
          {worstName} is the lowest-scoring {unitLabel.slice(0, -1)} at {worst[selectedMetric]}%.
        </b>{" "}
        Focus reviews on {lowBlocks.slice(0, 3).map((b) => rowLabel(b)).join(", ") || `priority ${unitLabel}`} and
        benchmark against {bestName || "top performers"}.
      </>
    );
  }, [
    worst,
    best,
    lowBlocks,
    highBlocks,
    sortedRows,
    selectedMetric,
    metricShort,
    isNarrowScope,
    scope,
    unitLabel,
    rowLabel,
  ]);

  const rankPreview = useMemo(() => {
    if (sortedRows.length <= 4) return sortedRows;
    const top2 = sortedRows.slice(0, 2);
    const bottom2 = [...sortedRows].slice(-2).reverse();
    return [...top2, ...bottom2];
  }, [sortedRows]);

  const defaultRoot = useMemo(
    () => [
      worst
        ? `${rowLabel(worst)} is the lowest-scoring ${unitLabel.slice(0, -1)} at ${worst[selectedMetric]}% on ${metricShort}.`
        : `Awaiting ${metricShort.toLowerCase()} metrics for the current filter scope.`,
      `${needsAction.length} ${unitLabel} need near-term follow-up; ${criticalCount} are in critical status.`,
      `${highBlocks.length} ${unitLabel} are above the high-performance threshold for ${metricShort.toLowerCase()}.`,
    ],
    [worst, needsAction, criticalCount, highBlocks, unitLabel, metricShort, selectedMetric, rowLabel],
  );

  const defaultRecs = useMemo(
    () => [
      `Inspect ${rowLabel(worst) || "priority"} schools and trigger a focused ${metricShort.toLowerCase()} review.`,
      `Compare ${lowBlocks.slice(0, 3).map((b) => rowLabel(b)).join(", ") || `low ${unitLabel}`} against ${rowLabel(best) || "top performers"}.`,
      `Use ${metricShort} map signals to prioritize field visits in the next 30-day review cycle.`,
    ],
    [worst, best, lowBlocks, unitLabel, metricShort, rowLabel],
  );

  const defaultActions = useMemo(
    () => [
      `Assign weekly block owners for ${metricShort.toLowerCase()} improvement across ${needsAction.length || "priority"} ${unitLabel}.`,
      `Publish a block-wise exception dashboard before the next district review.`,
      `Track movement on ${metricShort.toLowerCase()} until critical ${unitLabel} return above the 80% threshold.`,
    ],
    [metricShort, needsAction, unitLabel],
  );

  const barRows = useMemo(() => sortedRows.slice(0, 10), [sortedRows]);

  const tableRows = useMemo(() => sortedRows, [sortedRows]);

  const handleTalukaHover = (regionKey, event) => {
    setHovered(regionKey);
    if (!mapAreaRef.current) return;
    const rect = mapAreaRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left + 14;
    const y = event.clientY - rect.top + 14;
    setTooltip({
      visible: true,
      x: Math.min(x, rect.width - 220),
      y: Math.min(y, rect.height - 105),
    });
  };

  const handleTalukaLeave = () => {
    setHovered(null);
    setTooltip((t) => ({ ...t, visible: false }));
  };

  const hoveredVal = hovered ? metricValue(hovered) : null;
  const hoveredData = hovered ? dataByName.get(norm(hovered)) : null;
  const hoveredBand = getStatusBand(hoveredVal);
  const hoveredLabel = hovered ? SUBDISTRICTS[hovered]?.label || hovered : "";

  const confidencePct = useMemo(
    () => deriveMapConfidence(sortedRows, selectedMetric),
    [sortedRows, selectedMetric],
  );

  return (
    <div className="analytics-block-map-view" data-testid="analytics-block-map-tab">
      <DashboardPageHeader
        title={`${scopeLabel} ${metricShort} Map`}
        subtitle="A cleaner, decision-first view of block performance with the map, alerts, rankings and next actions visible without unnecessary scrolling."
        eyebrow={
          <>
            <svg className="aabm-svg-icon" style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20" />
              <path d="M2 12h20" />
              <circle cx="12" cy="12" r="8" />
            </svg>
            Executive Map View · AI-assisted block intelligence
          </>
        }
        actions={
          <div className="aabm-page-actions">
            <button type="button" className="aabm-btn">
              <svg className="aabm-svg-icon" style={{ width: 17, height: 17 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              Export
            </button>
            <button type="button" className="aabm-btn aabm-btn-primary" onClick={onRefresh}>
              <svg className="aabm-svg-icon" style={{ width: 17, height: 17 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
              Refresh Data
            </button>
          </div>
        }
      />

      <div className="aabm-insight-strip" aria-label="Executive summary">
        <article className="aabm-summary-card">
          <div className="aabm-summary-icon">
            <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5V19a3 3 0 1 1-3-3h6a3 3 0 1 1-3 3V4.5A2.5 2.5 0 0 1 14.5 2" />
              <path d="M8 8h8" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <div>
            <b>Today&apos;s readout</b>
            <span>{readout}</span>
          </div>
        </article>
        <article className="aabm-kpi-card">
          <div className="aabm-kpi-top">
            <small>{isNarrowScope ? "Blocks in scope" : "Blocks"}</small>
            <span className="aabm-trend">{monitoredCount}</span>
          </div>
          <strong>{monitoredCount}</strong>
          <span className="aabm-kpi-hint">{isNarrowScope ? "Filtered blocks" : "Talukas monitored"}</span>
        </article>
        <article className="aabm-kpi-card">
          <div className="aabm-kpi-top">
            <small>Schools</small>
            <span className="aabm-trend">Live</span>
          </div>
          <strong>{fmt(totalSchools)}</strong>
          <span className="aabm-kpi-hint">Active schools</span>
        </article>
        <article className="aabm-kpi-card">
          <div className="aabm-kpi-top">
            <small>Avg {selectedMetric === "shi_score" ? "OSH" : "Score"}</small>
            <span className="aabm-trend">{avgMetric >= 80 ? "Good" : avgMetric >= 70 ? "Watch" : "Alert"}</span>
          </div>
          <strong>{avgMetric || "—"}</strong>
          <span className="aabm-kpi-hint">{metricMeta.label}</span>
        </article>
        <article className="aabm-kpi-card">
          <div className="aabm-kpi-top">
            <small>Needs Action</small>
            <span className={`aabm-trend ${criticalCount ? "bad" : needsAction.length ? "warn" : ""}`}>
              {criticalCount ? `${criticalCount} Critical` : needsAction.length ? "Review" : "Clear"}
            </span>
          </div>
          <strong>{needsAction.length}</strong>
          <span className="aabm-kpi-hint">Priority blocks</span>
        </article>
      </div>

      <div className="aabm-tabbar-wrap aabm-tabbar-wrap--metric-only" aria-label="Map view options">
        <div className="aabm-metric-selector">
          <label htmlFor="aabm-metric">View by</label>
          <select id="aabm-metric" value={selectedMetric} onChange={(e) => onMetricChange(e.target.value)}>
            {METRIC_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="aabm-map-grid">
        <section className="aabm-panel aabm-map-panel" aria-label="Block performance map">
          <div className="aabm-panel-head">
            <div className="aabm-panel-title">
              <div className="aabm-title-icon">
                <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" />
                  <path d="M9 3v15M15 6v15" />
                </svg>
              </div>
              <div>
                <h2>Block Performance Map</h2>
                <p>
                  Color-coded school health across {regions.length} talukas · Last updated {fmtTime()}
                </p>
              </div>
            </div>
            <div className="aabm-map-tools">
              <button
                type="button"
                className="aabm-icon-btn"
                aria-label="Zoom in"
                onClick={handleZoomIn}
                disabled={zoom >= ZOOM_MAX}
              >
                <svg className="aabm-svg-icon aabm-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5v14" />
                </svg>
              </button>
              <button
                type="button"
                className="aabm-icon-btn"
                aria-label="Zoom out"
                onClick={handleZoomOut}
                disabled={zoom <= ZOOM_MIN}
              >
                <svg className="aabm-svg-icon aabm-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14" />
                </svg>
              </button>
              <button
                type="button"
                className="aabm-btn aabm-desktop-only"
                style={{ minHeight: 42 }}
                onClick={handleZoomReset}
                disabled={zoom === 1}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="aabm-map-area" ref={mapAreaRef} data-zoom={zoom}>
            <div className="aabm-map-svg-wrap">
              <svg
                className="aabm-map-svg"
                viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`}
                role="img"
                aria-label="Pune district block performance map"
              >
                <g className="aabm-map-zoom-layer" transform={mapTransform}>
                <g className="aabm-taluka-layer" fillRule="evenodd">
                  {regions.map((region) => {
                    const val = metricValue(region.key);
                    const band = getStatusBand(val);
                    const data = dataByName.get(norm(region.key));
                    const isHovered = hovered === region.key;
                    return (
                      <path
                        key={region.key}
                        className={`aabm-taluka ${band.statusClass || "aabm-status-nodata"}`}
                        d={region.path}
                        data-name={region.label}
                        data-score={val ?? ""}
                        data-schools={data?.schools ?? ""}
                        data-status={band.label}
                        onMouseEnter={(e) => handleTalukaHover(region.key, e)}
                        onMouseMove={(e) => handleTalukaHover(region.key, e)}
                        onMouseLeave={handleTalukaLeave}
                        onClick={() => {
                          if (data?.block_code) {
                            setScope({ blockCode: data.block_code, blockName: region.label });
                          }
                        }}
                        style={{
                          opacity:
                            isNarrowScope && val == null
                              ? 0.35
                              : hovered && !isHovered
                                ? 0.72
                                : 1,
                          cursor: data?.block_code ? "pointer" : "default",
                        }}
                      >
                        <title>
                          {region.label}: {val != null ? `${val}%` : "No data"}
                        </title>
                      </path>
                    );
                  })}
                </g>
                <g className="aabm-map-label" pointerEvents="none">
                  {regions.map((region) => {
                    const [lx, ly] = region.labelPoint || [];
                    if (typeof lx !== "number" || typeof ly !== "number") return null;
                    return (
                      <text key={`label-${region.key}`} x={lx} y={ly} fontSize={region.key === "PUNE CITY" ? 11 : 12}>
                        {region.label}
                      </text>
                    );
                  })}
                </g>
                </g>
              </svg>
            </div>

            <div className="aabm-map-meta">Actual ESRI polygon boundaries · {regions.length} talukas · WGS84</div>

            {tooltip.visible && hovered && (
              <div
                className="aabm-map-tooltip"
                style={{ left: tooltip.x, top: tooltip.y, display: "block" }}
                role="status"
                aria-live="polite"
              >
                <b>{hoveredLabel}</b>
                <span>
                  {metricShort} <strong>{hoveredVal != null ? `${hoveredVal}%` : "—"}</strong>
                </span>
                <span>
                  Schools <strong>{fmt(hoveredData?.schools)}</strong>
                </span>
                <span>
                  Status <strong>{hoveredBand.label}</strong>
                </span>
              </div>
            )}

            <div className="aabm-map-legend">
              <div className="aabm-legend-left">
                <span className="aabm-legend-title">Score bands</span>
                <span className="aabm-legend-item">
                  <i className="aabm-dot" style={{ background: "var(--aabm-danger)" }} />
                  Critical
                </span>
                <span className="aabm-legend-item">
                  <i className="aabm-dot" style={{ background: "var(--aabm-secondary)" }} />
                  Low
                </span>
                <span className="aabm-legend-item">
                  <i className="aabm-dot" style={{ background: "var(--aabm-warning)" }} />
                  Medium
                </span>
                <span className="aabm-legend-item">
                  <i className="aabm-dot" style={{ background: "var(--aabm-success)" }} />
                  High
                </span>
              </div>
              <div className="aabm-legend-right">
                <span className="aabm-legend-title">Performance</span>
                <span className="aabm-scale" />
                <span className="aabm-legend-title">Low → High</span>
              </div>
            </div>
          </div>
        </section>

        <aside className="aabm-side-panel" aria-label="Map insights">
          <article className="aabm-ai-card">
            <div className="aabm-ai-head">
              <div className="aabm-ai-badge">
                <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
              <div>
                <h3>AI Executive Insight</h3>
                <p className="aabm-ai-sub">Risk summary for current metric</p>
              </div>
            </div>
            <p className="aabm-ai-body">{aiInsight}</p>
            <div className="aabm-confidence">
              <div className="aabm-confidence-row">
                <span>Prediction confidence</span>
                <b>{confidencePct}%</b>
              </div>
              <div className="aabm-meter">
                <span style={{ width: `${confidencePct}%` }} />
              </div>
            </div>
          </article>

          <article className="aabm-panel aabm-mini-card">
            <h3>Top / Bottom Blocks</h3>
            <div className="aabm-rank-list">
              {rankPreview.map((row) => {
                const val = row[selectedMetric] ?? 0;
                const band = getStatusBand(val);
                const rowClass =
                  band.className === "critical" ? "bad" : band.className === "low" ? "warn" : "";
                const label = rowLabel(row);
                return (
                  <div className={`aabm-rank-row ${rowClass}`} key={`${row.block_name}-${val}`}>
                    <span>{label}</span>
                    <div className="aabm-rank-line">
                      <span style={{ width: `${Math.min(100, Math.max(8, val))}%` }} />
                    </div>
                    <b>{val}</b>
                  </div>
                );
              })}
            </div>
          </article>

        </aside>
      </div>

      <section className="aabm-action-plan-section">
        <InterventionActionPlan
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="aabm-lower-grid">
        <section className="aabm-panel aabm-chart-box" aria-label="Block performance ranking">
          <div className="aabm-section-head">
            <div>
              <h2>Block Performance Ranking</h2>
              <p>Top {Math.min(10, sortedRows.length)} {unitLabel} by selected metric</p>
            </div>
            <span className="aabm-trend">Avg {avgMetric || "—"}</span>
          </div>
          <div className="aabm-bars">
            {barRows.map((row) => {
              const val = row[selectedMetric] ?? 0;
              const band = getStatusBand(val);
              const fillClass =
                band.className === "critical"
                  ? "red"
                  : band.className === "low" || band.className === "medium"
                    ? "orange"
                    : "";
              const label = rowLabel(row);
              return (
                <div className="aabm-bar-row" key={`bar-${row.block_name}`}>
                  <span>{label}</span>
                  <div className="aabm-bar-track">
                    <div className={`aabm-bar-fill ${fillClass}`} style={{ width: `${Math.min(100, val)}%` }} />
                  </div>
                  <b>{val}</b>
                </div>
              );
            })}
          </div>
        </section>

        <section className="aabm-panel aabm-table-box" aria-label="Block details table">
          <div className="aabm-section-head">
            <div>
              <h2>Block Details</h2>
              <p>
                {isNarrowScope
                  ? `${tableRows.length} block${tableRows.length === 1 ? "" : "s"} in current filter scope`
                  : `All ${regions.length} talukas from the uploaded boundary file`}
              </p>
            </div>
            <button type="button" className="aabm-btn" style={{ minHeight: 38 }}>
              View All
            </button>
          </div>
          <div className="aabm-table-wrap">
            <table className="aabm-table">
              <thead>
                <tr>
                  <th>Block</th>
                  <th>Schools</th>
                  <th>{selectedMetric === "shi_score" ? "OSH" : "CHI"}</th>
                  <th>Status</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, idx) => {
                  const val = row[selectedMetric];
                  const band = getStatusBand(val);
                  const label = rowLabel(row);
                  return (
                    <tr key={`table-${row.block_name}`}>
                      <td>{label}</td>
                      <td>{fmt(row.schools)}</td>
                      <td>{val ?? "—"}</td>
                      <td>
                        <span className={`aabm-status ${band.className}`}>{band.label}</span>
                      </td>
                      <td>BEO-{String(idx + 1).padStart(2, "0")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="aabm-quick-footer">
        <article className="aabm-panel aabm-footer-card">
          <div className="aabm-title-icon">
            <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
          <div>
            <strong>Data Quality</strong>
            <span>
              {sortedRows.length
                ? `${Math.round((sortedRows.filter((t) => t[selectedMetric] != null).length / Math.max(monitoredCount, 1)) * 100)}% records complete across ${scopeLabel}.`
                : "Awaiting block metrics for the current filter."}
            </span>
          </div>
        </article>
        <article className="aabm-panel aabm-footer-card">
          <div className="aabm-title-icon">
            <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div>
            <strong>Review Cadence</strong>
            <span>Next district review due in 3 days.</span>
          </div>
        </article>
        <article className="aabm-panel aabm-footer-card">
          <div className="aabm-title-icon">
            <svg className="aabm-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18" />
              <path d="m19 9-5 5-4-4-3 3" />
            </svg>
          </div>
          <div>
            <strong>Improvement Trend</strong>
            <span>
              {highBlocks.length} blocks above high threshold for {metricShort.toLowerCase()}.
            </span>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AnalyticsBlockMapTab;
